import { gql } from '@apollo/client'
import { pathOr } from 'ramda'
import React from 'react'

import { serviceOrdersClient } from '../../../../utils/apollo/api'
import { setProjectId } from '../../../../utils/session/sessionStorage'
import { useQuery } from '../../../../utils/apollo/_helpers/useQuery'
import StatePresenter from '../../../../components/helpers/StatePresenter'
import { getQueryParam } from '../../../../utils/helpers/url'
import { getLocalStorageItem, removeLocalStorageItem } from '../../../../utils/helpers/localStorage'

const Context = React.createContext([])
const { Provider } = Context

export const useDynamicServiceOrdersState = () => React.useContext(Context)
export const useDynamicServiceOrders = () => useDynamicServiceOrdersState()?.dynamicServiceOrder
export const useQuestions = () => pathOr([], ['checklist', 'questions'], useDynamicServiceOrders())
export const useReplies = () => pathOr([], ['replies'], useDynamicServiceOrders())

const QUERY = gql`
  query DynamicServiceOrder($shareCode: String) {
    dynamicServiceOrder(shareCode: $shareCode) {
      id
      name
      projectId
      description
      redirectToExecution
      checklistId
      themeColor
      configuration
      logo {
        id
        src
      }
      place {
        id
        name
      }
      checklist {
        id
        questions {
          id
          name
          reference
          parentReference
          data
          displayConditions
          ordination
          categoryOrdination
          description
          documents {
            id
            src
            type
            description
          }
          masks {
            id
            name
            pattern
          }
          required
          allowNull
          photosEnabled
          photosRequired
          notesEnabled
          notesRequired
          defaultValue
          options {
            id
            name
            reference
            ordination
          }
          category {
            id
            name
          }
          type {
            id
            name
          }
        }
      }
    }
  }
`

const DynamicServiceOrdersLoader = ({ children, shareCode }) => {
  const placeId = getQueryParam('placeId')
  const assetId = getQueryParam('assetId')
  const { data, ...state } = useQuery(QUERY, {
    variables: { shareCode },
    client: serviceOrdersClient,
    fetchPolicy: 'no-cache'
  })

  const placeFromAPI = data?.dynamicServiceOrder?.place
  const assetFromStorage = getLocalStorageItem('asset')

  const [asset] = React.useState(assetFromStorage ?? (assetId ? { asset: { id: assetId } } : undefined))

  // When the activity is launched from an Asset's Linktree, the asset carried in `asset`
  // already includes its associated place (see qrcodes/_load/AssetLoader.js, which fetches
  // `place { id name resume }`). Fall back to it so the activity's place gets filled in even
  // though the Asset Linktree URL only tags the link with `assetId=` (no `placeId=`).
  const assetPlace = asset?.asset?.place
  const resolvedPlace = placeFromAPI ?? assetPlace ?? (placeId ? { id: placeId } : undefined)

  const [place] = React.useState(resolvedPlace ? { place: resolvedPlace } : undefined)

  const projectId = data?.dynamicServiceOrder?.projectId
  React.useEffect(() => {
    if (projectId) setProjectId(projectId)
    if (assetFromStorage) removeLocalStorageItem('asset')
  }, [projectId, assetFromStorage])

  const result = { dynamicServiceOrder: { ...data?.dynamicServiceOrder, ...place } }

  if (asset) {
    result.dynamicServiceOrder.asset = asset.asset
  }

  return (
    <StatePresenter {...state}>
      <Provider value={result}>{children}</Provider>
    </StatePresenter>
  )
}

export default DynamicServiceOrdersLoader
